import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../utils/config";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import AjudaCataleg from "./AjudaCataleg";
import { BotoAjuda } from "./AjudaModal";

function dataISO(v) {
  if (!v) return "";
  return String(v).slice(0, 10);
}

function etiquetaProveidor(prov) {
  if (prov.TipusEntitat === "Empresa") return prov.Nom;
  const cognoms = [prov.Cognom1, prov.Cognom2].filter(Boolean).join(" ");
  return cognoms ? `${cognoms}, ${prov.Nom}` : prov.Nom;
}

function DetallsProveidorProducte({ mode, producte, proveidorProd, onSave, onCancel, onCanviarMode, subnav }) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  TeclaEscEnter(onCancel, null);

  useEffect(() => {
    if (mode !== "consultar") setMenuAccions(false);
  }, [mode]);

  const [llistaProveidors, setLlistaProveidors] = useState([]);
  const [proveidorId, setProveidorId] = useState(null);
  const [preuPVPUnitari, setPreuPVPUnitari] = useState("");
  const [dteProveidor, setDteProveidor] = useState("");
  const [dataAlta, setDataAlta] = useState("");
  const [dataBaixa, setDataBaixa] = useState("");
  const [actiu, setActiu] = useState(true);
  const [preuNet, setPreuNet] = useState("");
  const [preuNetManual, setPreuNetManual] = useState(false);
  const [oferta, setOferta] = useState(false);
  const [preuOferta, setPreuOferta] = useState("");
  const [dataIniciOferta, setDataIniciOferta] = useState("");
  const [dataFinalOferta, setDataFinalOferta] = useState("");

  const resetForm = useCallback(() => {
    setProveidorId(null);
    setPreuPVPUnitari("");
    setDteProveidor("");
    setDataAlta(mode === "nou" ? new Date().toISOString().split("T")[0] : "");
    setDataBaixa("");
    setActiu(true);
    setPreuNet("");
    setPreuNetManual(false);
    setOferta(false);
    setPreuOferta("");
    setDataIniciOferta("");
    setDataFinalOferta("");
  }, [mode]);

  useEffect(() => {
    const fetchProveidors = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const response = await fetch(`${API_URL}/Proveidors.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const data = await response.json();
        setLlistaProveidors(data.success ? data.proveidors || [] : []);
      } catch (error) {
        setLlistaProveidors([]);
      }
    };
    fetchProveidors();
  }, [empresaSeleccionada]);

  useEffect(() => {
    if (proveidorProd) {
      setProveidorId(proveidorProd.Proveidors_idProveidors || null);
      setPreuPVPUnitari(proveidorProd.PreuPVPUnitari ?? "");
      setDteProveidor(proveidorProd.DteProveidor ?? "");
      setDataAlta(dataISO(proveidorProd.DataAlta));
      setDataBaixa(dataISO(proveidorProd.DataBaixa));
      setActiu(!!proveidorProd.Actiu);
      setPreuNet(proveidorProd.PreuNet ?? "");
      setPreuNetManual(true);
      setOferta(!!proveidorProd.Oferta);
      setPreuOferta(proveidorProd.PreuOferta ?? "");
      setDataIniciOferta(dataISO(proveidorProd.DataIniciOferta));
      setDataFinalOferta(dataISO(proveidorProd.DataFinalOferta));
    } else {
      resetForm();
    }
  }, [proveidorProd, mode, resetForm]);

  useEffect(() => {
    if (dataBaixa) setActiu(false);
  }, [dataBaixa]);

  useEffect(() => {
    if (!preuNetManual) {
      const pvp = parseFloat(preuPVPUnitari) || 0;
      const dte = parseFloat(dteProveidor) || 0;
      setPreuNet((pvp * (1 - dte / 100)).toFixed(2));
    }
  }, [preuPVPUnitari, dteProveidor, preuNetManual]);

  const payload = () => ({
    Productes_idProductes: producte.idProductes,
    Proveidors_idProveidors: proveidorId,
    Empresa_idEmpresa: empresaSeleccionada.IdEmpresa,
    PreuPVPUnitari: parseFloat(preuPVPUnitari) || 0,
    DteProveidor: parseFloat(dteProveidor) || 0,
    DataAlta: dataAlta || null,
    DataBaixa: dataBaixa || null,
    Actiu: actiu ? 1 : 0,
    PreuNet: parseFloat(preuNet) || 0,
    Oferta: oferta ? 1 : 0,
    PreuOferta: parseFloat(preuOferta) || 0,
    DataIniciOferta: dataIniciOferta || null,
    DataFinalOferta: dataFinalOferta || null,
  });

  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      if (!proveidorId) {
        alert("Has de seleccionar un proveïdor.");
        return;
      }
      if (!preuPVPUnitari) {
        alert("Cal indicar un Preu PVP unitari.");
        return;
      }
    }

    const dades =
      mode === "esborrar"
        ? { ...payload(), Actiu: 0, DataBaixa: dataBaixa || new Date().toISOString().split("T")[0] }
        : payload();

    const url =
      mode === "nou"
        ? `${API_URL}/Nouproveidorproducte.php`
        : `${API_URL}/Modificarproveidorproducte.php`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dades),
      });
      const result = await response.json();
      if (result.success) {
        onSave?.();
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error("Error gestió proveïdor-producte:", error);
      alert("S'ha produït un error en el procés.");
    }
  };

  const titol =
    mode === "nou"
      ? "Nou preu de proveïdor"
      : mode === "esborrar"
      ? "Desactivar preu"
      : mode === "modificar"
      ? "Modificar preu"
      : "Preu de proveïdor";

  return (
    <form className="dv2-entity" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaCataleg pestanya="preus" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "€"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">
              {[producte?.Marca, producte?.Model].filter(Boolean).join(" ")}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Preus de proveïdor" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && pucEsborrar && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.("esborrar");
                    }}
                  >
                    Desactivar
                  </button>
                </div>
              )}
            </div>
          )}
          {(mode === "nou" || mode === "modificar") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          )}
          {mode === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Desactivar
            </button>
          )}
        </div>
      </div>
      {subnav}

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          No s'esborra l'històric: el preu es desactiva amb data de baixa i deixa de ser el vigent.
        </div>
      )}

      <div className="dv2-entity-body">
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Tarifa</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <label className="dv2-field dv2-field--llarg">
            <span className={`dv2-label ${isReadOnly ? "" : "dv2-required"}`}>Proveïdor</span>
            {isReadOnly ? (
              <span className="dv2-value">
                {etiquetaProveidor(
                  llistaProveidors.find((p) => Number(p.idProveidors) === Number(proveidorId)) ||
                    { Nom: proveidorProd?.Nom, Cognom1: proveidorProd?.Cognom1, Cognom2: proveidorProd?.Cognom2, TipusEntitat: proveidorProd?.TipusEntitat }
                ) || "—"}
              </span>
            ) : (
              <select
                className="dv2-select"
                value={proveidorId || ""}
                onChange={(e) => setProveidorId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Selecciona un proveïdor</option>
                {llistaProveidors.map((prov) => (
                  <option key={prov.idProveidors} value={prov.idProveidors}>
                    {etiquetaProveidor(prov)}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label className="dv2-field dv2-field--curt">
            <span className={`dv2-label ${isReadOnly ? "" : "dv2-required"}`}>PVP unitari</span>
            {isReadOnly ? (
              <span className="dv2-value">{preuPVPUnitari || "—"}</span>
            ) : (
              <input
                type="number"
                step="0.01"
                className="dv2-input"
                value={preuPVPUnitari}
                onChange={(e) => {
                  setPreuPVPUnitari(e.target.value);
                  setPreuNetManual(false);
                  setDataAlta(new Date().toISOString().split("T")[0]);
                }}
              />
            )}
          </label>
          <label className="dv2-field dv2-field--curt">
            <span className="dv2-label">Dte. proveïdor (%)</span>
            {isReadOnly ? (
              <span className="dv2-value">{dteProveidor || "0"}</span>
            ) : (
              <input
                type="number"
                step="0.01"
                className="dv2-input"
                value={dteProveidor}
                onChange={(e) => {
                  setDteProveidor(e.target.value);
                  setPreuNetManual(false);
                  setDataAlta(new Date().toISOString().split("T")[0]);
                }}
              />
            )}
          </label>
          <label className="dv2-field dv2-field--mig">
            <span className="dv2-label">Preu net</span>
            {isReadOnly ? (
              <span className="dv2-value">{preuNet || "—"}</span>
            ) : (
              <span className="dv2-toggleRow">
                <input
                  type="number"
                  step="0.01"
                  className="dv2-input"
                  value={preuNet}
                  onChange={(e) => {
                    setPreuNet(e.target.value);
                    setPreuNetManual(true);
                  }}
                />
                <button type="button" className="dv2-btn" title="Recalcular a partir del PVP i el descompte" onClick={() => setPreuNetManual(false)}>
                  Recalc.
                </button>
              </span>
            )}
          </label>
          <label className="dv2-field dv2-field--mig">
            <span className="dv2-label">Data alta</span>
            {isReadOnly ? (
              <span className="dv2-value">{dataAlta || "—"}</span>
            ) : (
              <input type="date" className="dv2-input" value={dataAlta} onChange={(e) => setDataAlta(e.target.value)} />
            )}
          </label>
          {mode !== "nou" && (
            <label className="dv2-field dv2-field--mig">
              <span className="dv2-label">Data baixa</span>
              {isReadOnly ? (
                <span className="dv2-value">{dataBaixa || "—"}</span>
              ) : (
                <input
                  type="date"
                  className="dv2-input"
                  value={dataBaixa}
                  onChange={(e) => {
                    setDataBaixa(e.target.value);
                    if (!e.target.value) setActiu(true);
                  }}
                />
              )}
            </label>
          )}
          <label className="dv2-field dv2-field--curt">
            <span className="dv2-label">Actiu</span>
            {isReadOnly ? (
              <span className={`dv2-chip ${actiu ? "dv2-chip--ok" : ""}`}>{actiu ? "Sí" : "No"}</span>
            ) : (
              <span className="dv2-toggleRow">
                <span className="dv2-toggle">
                  <input type="checkbox" checked={actiu} onChange={(e) => setActiu(e.target.checked)} />
                  <span className="dv2-toggle-track" />
                </span>
                {actiu ? "Sí" : "No"}
              </span>
            )}
          </label>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">2</span>
          <h2 className="dv2-card-title">Oferta</h2>
        </div>
        {isReadOnly && !oferta ? (
          <p className="dv2-empty">No hi ha oferta vigent.</p>
        ) : (
          <>
            {!isReadOnly && (
              <div style={{ marginBottom: 12 }}>
                {!oferta ? (
                  <button
                    type="button"
                    className="dv2-btn dv2-btn--primary"
                    onClick={() => {
                      setOferta(true);
                      if (!dataIniciOferta) setDataIniciOferta(new Date().toISOString().split("T")[0]);
                    }}
                  >
                    Afegir oferta
                  </button>
                ) : (
                  <button type="button" className="dv2-btn" onClick={() => setOferta(false)}>
                    Treure oferta
                  </button>
                )}
              </div>
            )}
            {oferta && (
              <div className="dv2-fields dv2-fields--sizes">
                <label className="dv2-field dv2-field--mig">
                  <span className="dv2-label">Preu oferta</span>
                  {isReadOnly ? (
                    <span className="dv2-value">{preuOferta || "—"}</span>
                  ) : (
                    <input type="number" step="0.01" className="dv2-input" value={preuOferta} onChange={(e) => setPreuOferta(e.target.value)} />
                  )}
                </label>
                <label className="dv2-field dv2-field--mig">
                  <span className="dv2-label">Inici</span>
                  {isReadOnly ? (
                    <span className="dv2-value">{dataIniciOferta || "—"}</span>
                  ) : (
                    <input type="date" className="dv2-input" value={dataIniciOferta} onChange={(e) => setDataIniciOferta(e.target.value)} />
                  )}
                </label>
                <label className="dv2-field dv2-field--mig">
                  <span className="dv2-label">Final</span>
                  {isReadOnly ? (
                    <span className="dv2-value">{dataFinalOferta || "—"}</span>
                  ) : (
                    <input type="date" className="dv2-input" value={dataFinalOferta} onChange={(e) => setDataFinalOferta(e.target.value)} />
                  )}
                </label>
              </div>
            )}
          </>
        )}
      </section>
      </div>

      {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          <div className="dv2-footerActions">
            <button type="button" className="dv2-btn" onClick={onCancel}>
              Cancel·lar
            </button>
            {mode === "esborrar" ? (
              <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                Desactivar preu
              </button>
            ) : (
              <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                Desar
              </button>
            )}
          </div>
        </div>
      )}
    </form>
  );
}

export default DetallsProveidorProducte;
